import { Box } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { AiTaskEmbeddedPromptOut, AiTaskOut } from '@repo/api-codegen';
import {
	listAiTasksQueryKey,
	useDeleteAiTask,
	useListAiTaskEmbeddedPrompts,
	useRunAiTask,
	useUpdateAiTask,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Icon, Text } from '@repo/foundations';
import { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Virtuoso } from 'react-virtuoso';
import { queryClient, useAuthUser } from '../../api';
import { SmallLoadingSpinner } from '../../components/LoadingSpinner';
import { openConfirmModal } from '../../components/ModalManager';
import { CreateAgentModal } from '../../pages/AIAgentsPage/CreateAgentModal';
import {
	invalidateAgentRunsCache,
	invalidateChatHistoryCache,
} from '../../pages/AIAgentsPage/utils';
import { AgentListItem } from './components/AgentListItem';
import { AgentRunHistoryModal } from './components/AgentRunHistoryModal';
import { AgentsEmptyState } from './components/AgentsEmptyState';
import { AiAgentsHistorySkeleton } from './components/AiAgentsHistorySkeleton';
import { useInfiniteAiTasks } from './hooks/useInfiniteAiTasks';

function InternalAiAgentsController() {
	const navigate = useNavigate();
	const { user, workspace } = useAuthUser();
	const infinite = useInfiniteAiTasks();
	const items = useMemo(
		() => infinite.data?.pages ?? [],
		[infinite.data?.pages]
	);

	const [modalOpened, setModalOpened] = useState(false);
	const [editingAgent, setEditingAgent] = useState<AiTaskOut | null>(null);
	const [historyTaskId, setHistoryTaskId] = useState<string | null>(null);

	const runsQuery = useListAiTaskEmbeddedPrompts(
		{ pathParams: { taskId: historyTaskId ?? '' }, queryParams: {} },
		{ enabled: !!historyTaskId }
	);
	const runs: AiTaskEmbeddedPromptOut[] | undefined = runsQuery.data?.results;

	const activeAgentTitle: string | undefined = useMemo(
		() => items.find((agent) => agent.id === historyTaskId)?.title,
		[items, historyTaskId]
	);

	const { mutateAsync: runAgent } = useRunAiTask({
		onSuccess: async (_data, variables) => {
			const taskId = variables?.pathParams?.taskId ?? '';
			await Promise.all([
				invalidateAgentRunsCache(taskId),
				invalidateChatHistoryCache(),
			]);
			showNotification({
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
				title: 'Agent executed',
				message: 'The agent run was started successfully.',
			});
		},
	});

	const { mutateAsync: updateAgent } = useUpdateAiTask({
		onSuccess: () => {
			// Invalidate all AI-related queries to ensure fresh data
			queryClient.invalidateQueries();
		},
	});

	const { mutateAsync: deleteAgent } = useDeleteAiTask({
		onSuccess: async (_data, variables) => {
			const taskId = variables?.pathParams?.taskId ?? '';
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ['ai', 'tasks', 'infinite'],
				}),
				queryClient.invalidateQueries({
					queryKey: listAiTasksQueryKey({ queryParams: {} }),
				}),
				invalidateAgentRunsCache(taskId),
			]);
			showNotification({
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
				title: 'Agent deleted',
				message: 'The agent has been deleted successfully.',
			});
		},
	});

	const handleOpenModal = useCallback((agent?: AiTaskOut) => {
		if (agent) {
			setEditingAgent(agent);
		} else {
			setEditingAgent(null);
		}
		setModalOpened(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setModalOpened(false);
		setEditingAgent(null);
	}, []);

	const handleRun = useCallback(
		async (agent: AiTaskOut) => {
			trackEvent(
				'ai/agent/run',
				{
					agent_id: agent.id,
					agent_title: agent.title,
					trigger_location: 'sidebar',
				},
				user,
				workspace
			);

			let result;
			try {
				result = await runAgent({ pathParams: { taskId: agent.id } });
			} catch {
				showNotification({
					color: 'red',
					icon: (
						<Icon name="alertCircle" color="text/critical-on-fill/default" />
					),
					title: 'Run failed',
					message: 'There was an error running the agent.',
				});
				return;
			}

			if (result?.embedded_prompt_id) {
				await Promise.all([
					invalidateChatHistoryCache(),
					invalidateAgentRunsCache(agent.id),
				]);
				navigate(`/ai/${result.embedded_prompt_id}`);
			}
		},
		[runAgent, navigate, user, workspace]
	);

	const handleDelete = useCallback(
		(agent: AiTaskOut) => {
			openConfirmModal({
				title: 'Delete agent?',
				size: 360,
				centered: true,
				children: <Text size="sm">This can't be undone.</Text>,
				labels: { confirm: 'Delete', cancel: 'Cancel' },
				confirmProps: { variant: 'primary', tone: 'critical' },
				onConfirm: async () => {
					trackEvent(
						'ai/agent/delete',
						{
							agent_id: agent.id,
							trigger_location: 'sidebar',
						},
						user,
						workspace
					);

					try {
						await deleteAgent({ pathParams: { taskId: agent.id } });
					} catch {
						showNotification({
							color: 'red',
							icon: (
								<Icon
									name="alertCircle"
									color="text/critical-on-fill/default"
								/>
							),
							title: 'Delete failed',
							message: 'There was an error deleting the agent.',
						});
					}
				},
			});
		},
		[deleteAgent, user, workspace]
	);

	const handleEdit = useCallback(
		(agent: AiTaskOut) => {
			trackEvent(
				'ai/agent/edit',
				{
					agent_id: agent.id,
					trigger_location: 'sidebar',
				},
				user,
				workspace
			);

			handleOpenModal(agent);
		},
		[handleOpenModal, user, workspace]
	);

	const handleViewHistory = useCallback(
		(agent: AiTaskOut) => {
			trackEvent(
				'ai/agent/view_history',
				{
					agent_id: agent.id,
					trigger_location: 'sidebar',
				},
				user,
				workspace
			);

			setHistoryTaskId(agent.id);
		},
		[user, workspace]
	);

	const handleTogglePause = useCallback(
		(agent: AiTaskOut) => {
			updateAgent({
				pathParams: { taskId: agent.id },
				body: { is_enabled: !agent.is_enabled },
			});
		},
		[updateAgent, user, workspace]
	);

	const handleLearnMore = useCallback(() => {
		window.open(
			'https://docs.secoda.com/features/ai-assistant/secoda-ai-agents',
			'_blank'
		);
	}, []);

	const renderAgentItem = useCallback(
		(index: number, agent: AiTaskOut) => (
			<AgentListItem
				key={agent.id}
				agent={agent}
				onRun={handleRun}
				onEdit={handleEdit}
				onViewHistory={handleViewHistory}
				onDelete={handleDelete}
				onTogglePause={handleTogglePause}
			/>
		),
		[handleRun, handleEdit, handleViewHistory, handleDelete, handleTogglePause]
	);

	const renderFooter = useCallback(() => {
		if (infinite.isFetchingNextPage) {
			return <SmallLoadingSpinner />;
		}
		return null;
	}, [infinite.isFetchingNextPage]);

	if (infinite.isLoading) {
		return <AiAgentsHistorySkeleton />;
	}

	if (!items.length) {
		return (
			<>
				<AgentsEmptyState
					onCreate={() => handleOpenModal()}
					onLearnMore={handleLearnMore}
				/>
				{modalOpened && (
					<CreateAgentModal onClose={handleCloseModal} agent={editingAgent} />
				)}
			</>
		);
	}

	return (
		<Box h="100%" pl="sm" pb="md" mr="sm">
			<Virtuoso
				data={items}
				endReached={() => {
					if (infinite.hasNextPage && !infinite.isFetchingNextPage) {
						infinite.fetchNextPage();
					}
				}}
				itemContent={renderAgentItem}
				components={{
					Footer: renderFooter,
				}}
			/>
			{modalOpened && (
				<CreateAgentModal onClose={handleCloseModal} agent={editingAgent} />
			)}
			{historyTaskId && (
				<AgentRunHistoryModal
					opened={true}
					onClose={() => setHistoryTaskId(null)}
					runs={runs}
					isLoading={runsQuery.isLoading}
					agentTitle={activeAgentTitle}
				/>
			)}
		</Box>
	);
}

export const AiAgentsController = memo(InternalAiAgentsController);
